<script setup>
/* global axios */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const store = useStore();
const alert = useAlert();
const { t } = useI18n();
const BASE_KEY = 'AILAB.ASSISTANT_V2.DECISION_LOG';

const accountId = computed(() => route.params.accountId);
const assistantId = computed(() => props.assistant?.id);

const isLoading = ref(false);
const items = ref([]);
const meta = ref({ total: 0, limit: 50, offset: 0 });

const filters = ref({
  decision_type_like: '',
  inbox_id: '',
  risk_tier: '',
  mode: '',
  assistant_status: '',
  delivery_mode: '',
  status: '',
  auto_send_allowed: '',
  article_scope_warning: '',
  attribute_key: '',
  attribute_value: '',
  attribute_override_label: '',
  attribute_override_action: '',
  attribute_override_match_type: '',
  guidance_section: '',
  guidance_card: '',
  guidance_scenario: '',
  forced_reply_language: '',
  communication_style_mode: '',
});

const expanded = ref(new Set());

const failureDialogRef = ref(null);
const taggingEvent = ref(null);
const tagging = ref(false);
const failureForm = ref({
  user_text: '',
  desired_text: '',
  ai_text: '',
  notes: '',
  create_template: false,
});

const connectedInboxes = computed(
  () => store.getters['ailabInboxes/getRecords'] || []
);
const inboxById = computed(() => store.getters['inboxes/getInbox']);

const resolveInboxName = id => {
  const num = Number(id);
  if (!Number.isFinite(num)) return '';
  const inbox = inboxById.value?.(num) || {};
  return inbox?.name || String(num);
};

const getPayload = e => (e && typeof e === 'object' ? e.payload || {} : {});

const buildTags = e => {
  const p = getPayload(e);
  const tags = [];
  const push = (key, label) => {
    const v = p?.[key];
    if (v === undefined || v === null || String(v).trim() === '') return;
    tags.push({ key, label: label ? `${label}:${v}` : String(v) });
  };

  push('mode');
  push('assistant_status');
  push('delivery_mode', 'delivery');
  if (
    p?.reply_mode !== undefined &&
    p?.reply_mode !== null &&
    String(p.reply_mode).trim() !== '' &&
    String(p.reply_mode) !== String(p?.delivery_mode || '')
  ) {
    tags.push({ key: 'reply_mode', label: `reply:${p.reply_mode}` });
  }
  push('auto_send_allowed', 'auto_send');
  push('effective_mode', 'eff');
  push('source_kind', 'src');
  push('risk_tier', 'tier');
  push('intent');
  push('action');
  push('reason');
  push('article_scope', 'scope');
  push('article_scope_warning', 'scope_warn');
  push('attribute_source', 'attr_src');
  push('attribute_override_label', 'override');
  push('attribute_override_key', 'override_key');
  push('attribute_override_action', 'override_action');
  push('attribute_override_match_type', 'override_match');
  if (p?.attribute_detected_count !== undefined) {
    tags.push({ key: 'attribute_detected_count', label: `attrs:${p.attribute_detected_count}` });
  }
  if (Array.isArray(p?.attribute_matched_keys)) {
    p.attribute_matched_keys.slice(0, 2).forEach(attributeKey => {
      if (!attributeKey) return;
      tags.push({ key: `attribute_key_${attributeKey}`, label: `attr:${attributeKey}` });
    });
  }
  push('forced_reply_language', 'forced_lang');
  push('communication_style_mode', 'style');
  push('series_id', 'series');
  push('response_kind', 'resp');
  push('response_segments_count', 'segments');
  if (Array.isArray(p?.guidance_used_sections)) {
    p.guidance_used_sections.slice(0, 2).forEach(section => {
      if (!section) return;
      tags.push({ key: `guidance_${section}`, label: `guide:${section}` });
    });
  }
  if (Array.isArray(p?.guidance_card_keys)) {
    p.guidance_card_keys.slice(0, 2).forEach(card => {
      if (!card) return;
      tags.push({ key: `guidance_card_${card}`, label: `card:${card}` });
    });
  }
  if (Array.isArray(p?.guidance_scenarios)) {
    p.guidance_scenarios.slice(0, 2).forEach(scenario => {
      const key = scenario?.key || scenario?.title;
      const label = scenario?.title || scenario?.key;
      if (!key || !label) return;
      tags.push({ key: `guidance_scenario_${key}`, label: `scenario:${label}` });
    });
  }
  if (p?.guidance_scenario_count !== undefined) {
    tags.push({
      key: 'guidance_scenario_count',
      label: `scenarios:${p.guidance_scenario_count}`,
    });
  }
  if (p?.guidance_card_count !== undefined) {
    tags.push({
      key: 'guidance_card_count',
      label: `cards:${p.guidance_card_count}`,
    });
  }
  if (p?.guidance_used_sections_count !== undefined) {
    tags.push({
      key: 'guidance_used_sections_count',
      label: `guidance:${p.guidance_used_sections_count}`,
    });
  }
  if (p?.segment_index !== undefined && p?.total_segments !== undefined) {
    tags.push({
      key: 'segment',
      label: `seg:${p.segment_index}/${p.total_segments}`,
    });
  }
  push('status');
  if (p?.timed_out !== undefined) {
    tags.push({
      key: 'timed_out',
      label: `timed_out:${p.timed_out}`,
    });
  }
  push('policy_links', 'links');
  if (p?.policy_allow_multi_segment !== undefined) {
    tags.push({
      key: 'policy_allow_multi_segment',
      label: `multi:${p.policy_allow_multi_segment}`,
    });
  }
  if (p?.policy_allow_links !== undefined) {
    tags.push({
      key: 'policy_allow_links',
      label: `allow_links:${p.policy_allow_links}`,
    });
  }
  return tags.slice(0, 10);
};

const toggleExpanded = id => {
  const next = new Set(expanded.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expanded.value = next;
};

const apiUrl = computed(() => {
  if (!accountId.value) return '';
  return `/api/v1/accounts/${accountId.value}/ailab/ai_decision_events`;
});

const failureTagsUrl = computed(() => {
  if (!accountId.value) return '';
  return `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags`;
});

const fetchEvents = async ({ offset } = {}) => {
  if (!assistantId.value) return;
  if (!apiUrl.value) return;
  isLoading.value = true;
  try {
    const params = {
      assistant_id: assistantId.value,
      limit: meta.value.limit || 50,
      offset: Number.isFinite(Number(offset))
        ? Number(offset)
        : meta.value.offset || 0,
    };
    if (filters.value.decision_type_like)
      params.decision_type_like = filters.value.decision_type_like.trim();
    if (filters.value.inbox_id) params.inbox_id = filters.value.inbox_id;
    if (filters.value.risk_tier)
      params.risk_tier = String(filters.value.risk_tier).trim().toUpperCase();
    if (filters.value.mode)
      params.mode = String(filters.value.mode).trim().toLowerCase();
    if (filters.value.assistant_status)
      params.assistant_status = String(filters.value.assistant_status)
        .trim()
        .toLowerCase();
    if (filters.value.delivery_mode)
      params.delivery_mode = String(filters.value.delivery_mode).trim().toLowerCase();
    if (filters.value.status)
      params.status = String(filters.value.status).trim().toLowerCase();
    if (filters.value.auto_send_allowed !== '')
      params.auto_send_allowed = filters.value.auto_send_allowed;
    if (filters.value.article_scope_warning)
      params.article_scope_warning = String(filters.value.article_scope_warning).trim();
    if (filters.value.attribute_key)
      params.attribute_key = String(filters.value.attribute_key).trim();
    if (filters.value.attribute_value)
      params.attribute_value = String(filters.value.attribute_value).trim();
    if (filters.value.attribute_override_label)
      params.attribute_override_label = String(
        filters.value.attribute_override_label
      ).trim();
    if (filters.value.attribute_override_action)
      params.attribute_override_action = String(
        filters.value.attribute_override_action
      ).trim();
    if (filters.value.attribute_override_match_type)
      params.attribute_override_match_type = String(
        filters.value.attribute_override_match_type
      ).trim();
    if (filters.value.guidance_section)
      params.guidance_section = String(filters.value.guidance_section).trim();
    if (filters.value.guidance_card)
      params.guidance_card = String(filters.value.guidance_card).trim();
    if (filters.value.guidance_scenario)
      params.guidance_scenario = String(filters.value.guidance_scenario).trim();
    if (filters.value.forced_reply_language)
      params.forced_reply_language = String(filters.value.forced_reply_language)
        .trim()
        .toLowerCase();
    if (filters.value.communication_style_mode)
      params.communication_style_mode = String(filters.value.communication_style_mode)
        .trim()
        .toLowerCase();

    const res = await axios.get(apiUrl.value, { params });
    items.value = res?.data?.data || [];
    meta.value = res?.data?.meta || {
      total: items.value.length,
      limit: 50,
      offset: 0,
    };
  } catch (e) {
    alert(
      e?.response?.data?.error ||
        e?.message ||
        t(`${BASE_KEY}.ALERTS.LOAD_FAILED`)
    );
  } finally {
    isLoading.value = false;
  }
};

const refresh = async () => {
  expanded.value = new Set();
  await fetchEvents({ offset: 0 });
};

const openFailureDialog = e => {
  taggingEvent.value = e;
  failureForm.value = {
    user_text: '',
    desired_text: '',
    ai_text: '',
    notes: `decision_type=${e?.decision_type || ''}`.trim(),
    create_template: false,
  };
  failureDialogRef.value?.open();
};

const submitFailureTag = async () => {
  if (!failureTagsUrl.value) return;
  const e = taggingEvent.value;
  if (!e?.id) return;
  tagging.value = true;
  try {
    await axios.post(failureTagsUrl.value, {
      ai_decision_event_id: e.id,
      assistant_id: e.assistant_id,
      inbox_id: e.inbox_id,
      conversation_id: e.conversation_id,
      user_text: failureForm.value.user_text,
      desired_text: failureForm.value.desired_text,
      ai_text: failureForm.value.ai_text,
      notes: failureForm.value.notes,
      create_template: failureForm.value.create_template,
    });
    alert(t(`${BASE_KEY}.ALERTS.TAGGED_AS_FAILURE`));
    failureDialogRef.value?.close();
  } catch (err) {
    alert(
      err?.response?.data?.error ||
        err?.message ||
        t(`${BASE_KEY}.ALERTS.TAG_FAILURE_FAILED`)
    );
  } finally {
    tagging.value = false;
  }
};

const prevPage = async () => {
  const nextOffset = Math.max(
    0,
    (meta.value.offset || 0) - (meta.value.limit || 50)
  );
  await fetchEvents({ offset: nextOffset });
};

const nextPage = async () => {
  const nextOffset = (meta.value.offset || 0) + (meta.value.limit || 50);
  if (nextOffset >= (meta.value.total || 0)) return;
  await fetchEvents({ offset: nextOffset });
};

onMounted(async () => {
  if (!assistantId.value) return;
  try {
    // For inbox name display
    await store.dispatch('ailabInboxes/get', {
      assistantId: assistantId.value,
    });
    await store.dispatch('inboxes/get');
  } catch {
    // ignore
  }
  await fetchEvents({ offset: 0 });
});
</script>

<template>
  <div class="decision-log-tab">
    <div class="tab-header">
      <div class="header-left">
        <div class="header-icon">
          <span class="i-lucide-scroll-text size-5" />
        </div>
        <div>
          <h1 class="text-lg font-bold text-slate-800 dark:text-white">
            {{ t(`${BASE_KEY}.HEADER.TITLE`) }}
          </h1>
          <p class="text-xs text-slate-500 dark:text-white/50 mt-0.5">
            {{ t(`${BASE_KEY}.HEADER.DESCRIPTION`) }}
          </p>
        </div>
      </div>
      <div class="header-actions">
        <button class="secondary-btn" :disabled="isLoading" @click="refresh">
          <span
            class="size-4"
            :class="[
              isLoading
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-refresh-cw',
            ]"
          />
          {{ t(`${BASE_KEY}.ACTIONS.REFRESH`) }}
        </button>
      </div>
    </div>

    <div class="card">
      <div class="filters">
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.INBOX`)
          }}</label>
          <select
            v-model="filters.inbox_id"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option v-for="ci in connectedInboxes" :key="ci.id" :value="ci.id">
              {{ ci.name || resolveInboxName(ci.id) }}
            </option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.MODE`)
          }}</label>
          <select
            v-model="filters.mode"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="ai_closer">{{ 'ai_closer' }}</option>
            <option value="ai_gamer">{{ 'ai_gamer' }}</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.DECISION_TYPE`)
          }}</label>
          <input
            v-model="filters.decision_type_like"
            class="filter-input"
            type="text"
            :placeholder="t(`${BASE_KEY}.FILTERS.DECISION_TYPE_PLACEHOLDER`)"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.RISK_TIER`)
          }}</label>
          <select
            v-model="filters.risk_tier"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="L0">{{ 'L0' }}</option>
            <option value="L1">{{ 'L1' }}</option>
            <option value="L2">{{ 'L2' }}</option>
            <option value="L3">{{ 'L3' }}</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.ASSISTANT_STATUS`)
          }}</label>
          <select
            v-model="filters.assistant_status"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="active">{{ 'active' }}</option>
            <option value="inactive">{{ 'inactive' }}</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.STATUS`)
          }}</label>
          <select
            v-model="filters.status"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="timed_out">{{ 'timed_out' }}</option>
            <option value="active">{{ 'active' }}</option>
            <option value="completed">{{ 'completed' }}</option>
            <option value="handed_off">{{ 'handed_off' }}</option>
            <option value="cancelled">{{ 'cancelled' }}</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.AUTO_SEND`)
          }}</label>
          <select
            v-model="filters.auto_send_allowed"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="true">{{ 'true' }}</option>
            <option value="false">{{ 'false' }}</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">{{
            t(`${BASE_KEY}.FILTERS.LABELS.REPLY_MODE`)
          }}</label>
          <select
            v-model="filters.delivery_mode"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="auto_reply">{{ 'auto_reply' }}</option>
            <option value="auto_suggest">{{ 'auto_suggest' }}</option>
            <option value="suggest">{{ 'suggest' }}</option>
            <option value="handoff">{{ 'handoff' }}</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">Article scope warning</label>
          <input
            v-model="filters.article_scope_warning"
            class="filter-input"
            type="text"
            placeholder="ambiguous_assistant_portals"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">Attribute key</label>
          <input
            v-model="filters.attribute_key"
            class="filter-input"
            type="text"
            placeholder="intent"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">Attribute value</label>
          <input
            v-model="filters.attribute_value"
            class="filter-input"
            type="text"
            placeholder="deposit"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">Override label</label>
          <input
            v-model="filters.attribute_override_label"
            class="filter-input"
            type="text"
            placeholder="Late payment override"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">Override action</label>
          <select
            v-model="filters.attribute_override_action"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="inherit">inherit</option>
            <option value="auto_suggest">auto_suggest</option>
            <option value="suggest">suggest</option>
            <option value="handoff">handoff</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">Override match type</label>
          <select
            v-model="filters.attribute_override_match_type"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="equals">equals</option>
            <option value="contains">contains</option>
            <option value="starts_with">starts_with</option>
            <option value="ends_with">ends_with</option>
            <option value="in">in</option>
            <option value="regex">regex</option>
            <option value="present">present</option>
            <option value="boolean">boolean</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">Guidance section</label>
          <input
            v-model="filters.guidance_section"
            class="filter-input"
            type="text"
            placeholder="instructions"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">Guidance card</label>
          <input
            v-model="filters.guidance_card"
            class="filter-input"
            type="text"
            placeholder="runtime_assembly"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">Guidance scenario</label>
          <input
            v-model="filters.guidance_scenario"
            class="filter-input"
            type="text"
            placeholder="legacy:support-flow:1"
            :disabled="isLoading"
          />
        </div>
        <div class="filter">
          <label class="filter-label">Forced reply language</label>
          <select
            v-model="filters.forced_reply_language"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="en">en</option>
            <option value="th">th</option>
            <option value="ms">ms</option>
          </select>
        </div>
        <div class="filter">
          <label class="filter-label">Communication style</label>
          <select
            v-model="filters.communication_style_mode"
            class="filter-input"
            :disabled="isLoading"
          >
            <option value="">{{ t(`${BASE_KEY}.FILTERS.ALL`) }}</option>
            <option value="friendly">friendly</option>
            <option value="professional">professional</option>
            <option value="casual">casual</option>
            <option value="formal">formal</option>
            <option value="adaptive">adaptive</option>
          </select>
        </div>
        <div class="filter-actions">
          <button
            class="primary-btn"
            :disabled="isLoading"
            @click="fetchEvents({ offset: 0 })"
          >
            {{ t(`${BASE_KEY}.ACTIONS.APPLY`) }}
          </button>
          <button
            class="secondary-btn"
            :disabled="isLoading"
            @click="
              filters = {
                decision_type_like: '',
                inbox_id: '',
                risk_tier: '',
                mode: '',
                assistant_status: '',
                delivery_mode: '',
                status: '',
                auto_send_allowed: '',
                article_scope_warning: '',
                attribute_key: '',
                attribute_value: '',
                attribute_override_label: '',
                attribute_override_action: '',
                attribute_override_match_type: '',
                guidance_section: '',
                guidance_card: '',
                guidance_scenario: '',
                forced_reply_language: '',
                communication_style_mode: '',
              };
              fetchEvents({ offset: 0 });
            "
          >
            {{ t(`${BASE_KEY}.ACTIONS.CLEAR`) }}
          </button>
        </div>
      </div>

      <div class="meta-row">
        <div class="meta-text">
          {{
            t(`${BASE_KEY}.META.SHOWING`, {
              shown: Math.min(
                (meta.offset || 0) + (items.length || 0),
                meta.total || 0
              ),
              total: meta.total || 0,
            })
          }}
        </div>
        <div class="pager">
          <button
            class="pager-btn"
            :disabled="isLoading || (meta.offset || 0) <= 0"
            @click="prevPage"
          >
            {{ t(`${BASE_KEY}.PAGER.PREV`) }}
          </button>
          <button
            class="pager-btn"
            :disabled="
              isLoading ||
              (meta.offset || 0) + (meta.limit || 50) >= (meta.total || 0)
            "
            @click="nextPage"
          >
            {{ t(`${BASE_KEY}.PAGER.NEXT`) }}
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="loading">
        <span class="i-lucide-loader-2 size-5 animate-spin text-orange-400" />
        <span>{{ t(`${BASE_KEY}.STATE.LOADING`) }}</span>
      </div>

      <div v-else-if="!items.length" class="empty">
        <span class="i-lucide-info size-5 text-slate-400" />
        <span>{{ t(`${BASE_KEY}.STATE.EMPTY`) }}</span>
      </div>

      <div v-else class="table">
        <div v-for="e in items" :key="e.id" class="row">
          <button class="row-main" @click="toggleExpanded(e.id)">
            <div class="row-left">
              <span class="pill">{{
                e.decision_type || t(`${BASE_KEY}.STATUS.UNKNOWN`)
              }}</span>
              <span class="muted">{{ e.source || '-' }}</span>
              <div class="tags">
                <span
                  v-for="t in buildTags(e)"
                  :key="t.key + t.label"
                  class="tag"
                  >{{ t.label }}</span
                >
              </div>
            </div>
            <div class="row-right">
              <span v-if="e.inbox_id" class="muted"
                >{{ t(`${BASE_KEY}.META.INBOX_LABEL`) }}
                {{ resolveInboxName(e.inbox_id) }}</span
              >
              <span class="muted">{{ e.created_at }}</span>
              <span
                class="i-lucide-chevron-down size-4"
                :class="[expanded.has(e.id) ? 'rot' : '']"
              />
            </div>
          </button>

          <div class="row-actions">
            <button
              class="secondary-btn"
              :disabled="isLoading"
              @click="openFailureDialog(e)"
            >
              {{ t(`${BASE_KEY}.ACTIONS.TAG_FAILURE`) }}
            </button>
          </div>

          <pre v-if="expanded.has(e.id)" class="payload">{{
            JSON.stringify(e.payload || {}, null, 2)
          }}</pre>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    ref="failureDialogRef"
    :title="t(`${BASE_KEY}.FAILURE_DIALOG.TITLE`)"
    :description="t(`${BASE_KEY}.FAILURE_DIALOG.DESCRIPTION`)"
    width="2xl"
    :confirm-button-label="t('AILAB.FORM.SAVE')"
    :cancel-button-label="t('AILAB.FORM.CANCEL')"
    :disable-confirm-button="tagging"
    :is-loading="tagging"
    @confirm="submitFailureTag"
  >
    <div class="text-sm text-n-slate-11">
      {{ t(`${BASE_KEY}.FAILURE_DIALOG.EVENT_LABEL`) }}
      <span class="text-n-slate-12">{{
        taggingEvent?.decision_type || '-'
      }}</span>
    </div>
    <div class="mt-3 grid grid-cols-1 gap-3">
      <div>
        <label class="filter-label">{{
          t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.CUSTOMER_MESSAGE`)
        }}</label>
        <textarea
          v-model="failureForm.user_text"
          class="filter-input"
          rows="3"
          :placeholder="
            t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.CUSTOMER_MESSAGE_PLACEHOLDER`)
          "
        />
      </div>
      <div>
        <label class="filter-label">{{
          t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.AI_REPLY`)
        }}</label>
        <textarea
          v-model="failureForm.ai_text"
          class="filter-input"
          rows="3"
          :placeholder="
            t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.AI_REPLY_PLACEHOLDER`)
          "
        />
      </div>
      <div>
        <label class="filter-label">{{
          t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.DESIRED_REPLY`)
        }}</label>
        <textarea
          v-model="failureForm.desired_text"
          class="filter-input"
          rows="3"
          :placeholder="
            t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.DESIRED_REPLY_PLACEHOLDER`)
          "
        />
      </div>
      <div>
        <label class="filter-label">{{
          t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.NOTES`)
        }}</label>
        <input
          v-model="failureForm.notes"
          class="filter-input"
          type="text"
          :placeholder="
            t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.NOTES_PLACEHOLDER`)
          "
        />
      </div>
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input v-model="failureForm.create_template" type="checkbox" />
        {{ t(`${BASE_KEY}.FAILURE_DIALOG.FIELDS.CREATE_TEMPLATE`) }}
      </label>
    </div>
  </Dialog>
</template>

<style scoped>
.decision-log-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding-bottom: 80px;
  --decision-icon-bg: #ffffff;
  --decision-icon-color: #000000;
  --decision-primary-bg: #ffffff;
  --decision-primary-color: #000000;
  --decision-primary-hover: #e5e7eb;
  --decision-row-bg: rgba(0, 0, 0, 0.1);
  --decision-row-actions-bg: rgba(0, 0, 0, 0.12);
  --decision-payload-bg: rgba(0, 0, 0, 0.25);
}

:global(body:not(.dark) .decision-log-tab) {
  --decision-icon-bg: rgba(0, 102, 255, 0.14);
  --decision-icon-color: #0066ff;
  --decision-primary-bg: #0066ff;
  --decision-primary-color: #ffffff;
  --decision-primary-hover: linear-gradient(135deg, #3388ff 0%, #0066ff 100%);
  --decision-row-bg: rgba(15, 23, 42, 0.03);
  --decision-row-actions-bg: rgba(15, 23, 42, 0.04);
  --decision-payload-bg: rgba(15, 23, 42, 0.04);
}

.tab-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
}

.header-left {
  display: flex;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--decision-icon-bg);
  border-radius: 12px;
  color: var(--decision-icon-color);
  flex-shrink: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card {
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filters {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr auto;
  gap: 12px;
  align-items: end;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--wlab-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--wlab-border-light);
  background: var(--wlab-bg-input);
  color: var(--wlab-text-primary);
  font-size: 12px;
}

/* Dark theme fix: select dropdowns should not be white-on-white */
:global(body.dark .filter-input) {
  background: rgba(0, 0, 0, 0.35);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.12);
}

:global(body.dark select.filter-input option) {
  background: #111827;
  color: #ffffff;
}

.filter-actions {
  display: flex;
  gap: 10px;
}

.primary-btn {
  padding: 10px 14px;
  border-radius: 10px;
  border: none;
  background: var(--decision-primary-bg);
  color: var(--decision-primary-color);
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
}

.primary-btn:hover {
  background: var(--decision-primary-hover);
}

.secondary-btn {
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid var(--wlab-border-light);
  background: transparent;
  color: var(--wlab-text-primary);
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid var(--wlab-border-light);
  padding-top: 12px;
}

.meta-text {
  font-size: 12px;
  color: var(--wlab-text-muted);
}

.pager {
  display: flex;
  gap: 8px;
}

.pager-btn {
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid var(--wlab-border-light);
  background: var(--wlab-bg-input);
  color: var(--wlab-text-primary);
  font-size: 12px;
  cursor: pointer;
}

.loading,
.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 12px;
  color: var(--wlab-text-muted);
  font-size: 12px;
}

.table {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.row {
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
  overflow: hidden;
  background: var(--decision-row-bg);
}

.row-actions {
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  border-top: 1px solid var(--wlab-border-light);
  background: var(--decision-row-actions-bg);
}

.row-main {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.row-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag {
  display: inline-flex;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(148, 163, 184, 0.08);
  color: var(--wlab-text-secondary);
  font-size: 10px;
  font-weight: 700;
  max-width: 100%;
}

.row-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.pill {
  display: inline-flex;
  width: fit-content;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0, 102, 255, 0.35);
  background: rgba(0, 102, 255, 0.12);
  color: rgba(0, 102, 255, 0.95);
  font-size: 11px;
  font-weight: 800;
}

.muted {
  font-size: 11px;
  color: var(--wlab-text-muted);
  white-space: nowrap;
}

.payload {
  margin: 0;
  padding: 12px;
  border-top: 1px solid var(--wlab-border-light);
  background: var(--decision-payload-bg);
  color: var(--wlab-text-primary);
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
}

.rot {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}
</style>
